import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Tab,
  Tabs,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import { observer } from "mobx-react-lite";
import { toast } from "react-toastify";
import RegisteredUser from "./RegisteredUser";
import PendingUser from "./PendingUser";
import userStore from "../../../stores/UserStore";

const UserManagement = observer(() => {
  const [tab, setTab] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "" });

  useEffect(() => {
    userStore.fetchAllUsers();
  }, []);

  const handleCreateUser = async () => {
    try {
      await userStore.createUser({
        name: newUser.name,
        username: newUser.email,
        password: newUser.password,
      });

      toast.success("User created successfully!");
      setAddOpen(false);
      setNewUser({ name: "", email: "", password: "" });

      await userStore.fetchAllUsers();
    } catch (err) {
      toast.error(err.message || "Failed to create user");
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card
        elevation={3}
        sx={{
          mb: 4,
          p: 2,
          background: "#F3F4F6",
          borderLeft: "4px solid #22C55E",
        }}
      >
        <CardContent>
          <Box display="flex" alignItems="center" mb={1}>
            <PersonIcon fontSize="large" sx={{ mr: 2, color: "#22C55E" }} />
            <Typography variant="h4" fontWeight="bold">
              {tab === 0 ? "Registered Users" : "Pending Users"}
            </Typography>
          </Box>
          <Typography color="text.secondary">
            {tab === 0
              ? "View and manage all active users currently registered in the system"
              : "Review newly registered users who are waiting for admin activation"}
          </Typography>
        </CardContent>
      </Card>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Button
          variant="contained"
          onClick={() => setAddOpen(true)}
          startIcon={<AddIcon />}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            backgroundColor: "#22C55E",
            color: "#FFFFFF",
            "&:hover": { backgroundColor: "#16A34A" },
          }}
        >
          Add New User
        </Button>
      </Box>

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        textColor="inherit"
        slotProps={{
          indicator: {
            sx: { backgroundColor: "#22C55E" },
          },
        }}
        sx={{
          mb: 2,
          "& .MuiTab-root": { color: "inherit", opacity: 1 },
          "& .MuiTab-root.Mui-selected": { color: "black", opacity: 1 },
        }}
      >
        <Tab className="!normal-case" label="Registered Users" />
        <Tab className="!normal-case" label="Pending Users" />
      </Tabs>

      {tab === 0 && <RegisteredUser />}
      {tab === 1 && <PendingUser />}

      <Dialog open={addOpen} onClose={() => setAddOpen(false)}>
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            fullWidth
            margin="dense"
            value={newUser.name}
            onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
          />
          <TextField
            label="Email"
            fullWidth
            margin="dense"
            value={newUser.email}
            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
          />
          <TextField
            label="Password"
            type="password"
            fullWidth
            margin="dense"
            value={newUser.password}
            onChange={(e) =>
              setNewUser({ ...newUser, password: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateUser}
            sx={{
              backgroundColor: "#22C55E",
              color: "#FFFFFF",
              "&:hover": { backgroundColor: "#16A34A" },
            }}
          >
            Create User
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default UserManagement;
